import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2, PackagePlus, Sparkles, X } from 'lucide-react';
import { apiService } from '../../services/api';

// Ce que Nexus déduit d'un achat pour les fiches projet : ce qui est sûr est noté, les hypothèses attendent la
// réponse de l'acheteur (ici ou dans Slack), les composants clés peuvent entrer au PLM.

const DONE = {
  sure: ['noté dans la fiche', 'bg-emerald-50 text-emerald-700'],
  confirmed: ['confirmé', 'bg-emerald-50 text-emerald-700'],
  corrected: ['précisé', 'bg-indigo-50 text-indigo-700'],
  rejected: ['écarté', 'bg-gray-100 text-gray-500'],
  added: ['ajouté au PLM', 'bg-emerald-50 text-emerald-700'],
  declined: ['pas au PLM', 'bg-gray-100 text-gray-500'],
};

const Chip = ({ status }) => {
  const [label, tone] = DONE[status] || [];
  return label ? <span className={`shrink-0 rounded-full px-1.5 py-px text-[10px] font-medium ${tone}`}>{label}</span> : null;
};

const Hypothesis = ({ item, onAnswer }) => {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (answer, text) => {
    setBusy(true);
    try {
      await onAnswer(item.id, answer, text);
      setDetail(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-gray-800">{item.question || item.text}</p>
      {detail === null ? (
        <div className="flex flex-wrap gap-1.5">
          <button type="button" disabled={busy} onClick={() => send('oui')}
            className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
            <Check className="h-3.5 w-3.5" /> Oui
          </button>
          <button type="button" disabled={busy} onClick={() => send('non')}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:border-gray-300 disabled:opacity-50">
            Non
          </button>
          <button type="button" disabled={busy} onClick={() => setDetail('')}
            className="rounded-md px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:opacity-50">
            Préciser…
          </button>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-violet-500" />}
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (detail.trim()) send('precision', detail.trim()); }} className="flex gap-1.5">
          <input autoFocus value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Ce qu'il en est vraiment…"
            className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-sm focus:border-violet-400 focus:outline-none" />
          <button type="submit" disabled={busy || !detail.trim()}
            className="rounded-md bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40">Envoyer</button>
          <button type="button" onClick={() => setDetail(null)} className="rounded-md p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </form>
      )}
    </div>
  );
};

const NexusInsights = ({ expenseId, recent }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const polls = useRef(0);

  const load = useCallback(() => apiService.get(`/expenses/${expenseId}/insights`)
    .then((d) => { setData(d); setError(null); return d; })
    .catch((err) => { setError(err.message); return null; }), [expenseId]);

  // Facture validée à l'instant : Nexus la relit en fond (quelques dizaines de secondes).
  useEffect(() => {
    polls.current = 0;
    let timer;
    const tick = async () => {
      const d = await load();
      polls.current += 1;
      if (recent && d && !d.items.length && polls.current < 12) timer = setTimeout(tick, 10000);
    };
    tick();
    return () => clearTimeout(timer);
  }, [load, recent]);

  const answer = async (id, verdict, text) => {
    await apiService.post(`/purchase-insights/${id}/answer`, { answer: verdict, text: text || null });
    await load();
  };
  const plm = async (id, add) => {
    await apiService.post(`/purchase-insights/${id}/plm`, { add });
    await load();
  };

  const items = data?.items || [];
  if (!items.length && !(recent && !error && polls.current < 12)) return null;
  const projects = [...new Set(items.map((i) => i.project_name))];

  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/40 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-violet-700">
        <Sparkles className="h-3.5 w-3.5" /> Ce que Nexus en déduit
      </p>
      {!items.length && (
        <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin text-violet-500" /> Nexus relit cet achat…</p>
      )}
      <div className="space-y-3">
        {projects.map((project) => (
          <div key={project} className="space-y-2">
            {projects.length > 1 && <p className="text-xs font-medium text-gray-500">{project}</p>}
            {items.filter((i) => i.project_name === project).map((item) => (
              <div key={item.id} className="rounded-md bg-white px-2.5 py-2 shadow-sm ring-1 ring-violet-100">
                {item.kind === 'fait' && item.status === 'pending' ? (
                  <Hypothesis item={item} onAnswer={answer} />
                ) : item.kind === 'fait' ? (
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 text-sm text-gray-700">
                      {item.text}
                      {item.answer && <span className="mt-0.5 block text-xs text-indigo-700">« {item.answer} »{item.answered_by_name ? ` (${item.answered_by_name})` : ''}</span>}
                    </p>
                    <Chip status={item.status} />
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <PackagePlus className="h-4 w-4 shrink-0 text-violet-500" />
                    <p className="min-w-0 flex-1 text-sm text-gray-700">
                      Ajouter <span className="font-medium">{item.text}</span> au PLM
                      {item.payload?.dans ? <> (dans « {item.payload.dans} »)</> : null}
                      {item.payload?.description && <span className="block text-xs text-gray-500">{item.payload.description}</span>}
                    </p>
                    {item.status === 'proposed' ? (
                      <span className="flex gap-1">
                        <button type="button" onClick={() => plm(item.id, true)}
                          className="rounded-md bg-violet-600 px-2 py-1 text-xs font-semibold text-white hover:bg-violet-700">Ajouter</button>
                        <button type="button" onClick={() => plm(item.id, false)}
                          className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100">Non merci</button>
                      </span>
                    ) : <Chip status={item.status} />}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default NexusInsights;
